import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";

const LOGO = "/logo-geekcreative.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_borrow-geek-design/artifacts/prsvz2g4_94dc33cd-3d3b-4e37-9cc4-5561534b9c43.png";

const DESIGN_SERVICES = [
  "Logo & Brand Identity",
  "Print Design",
  "Social Media Graphics",
  "Promotional Campaigns",
  "Business Stationery",
  "Signage & Display",
  "Packaging & Labels",
  "Print-Ready Artwork",
];

const WEB_SERVICES = [
  "Custom Website Design",
  "Landing Pages",
  "E-commerce Stores",
  "Website Redesigns",
  "Hosting & Maintenance",
  "SEO & Performance",
];

export default function LandingPreview() {
  return (
    <div className="preview-scope min-h-screen bg-white text-[#0B1240]">
      {/* Editorial theme injection — inherits Poppins from global CSS */}
      <style>{`
        .preview-scope { color: #0B1240; }
        .preview-scope .hairline { border-top: 1px solid #E7E4DD; }
        .preview-scope .muted { color: #6B7186; }
        .preview-scope .num { font-weight: 300; letter-spacing: -0.05em; }
        .preview-scope a.link-underline { position: relative; }
        .preview-scope a.link-underline::after { content:''; position:absolute; left:0; right:0; bottom:-2px; height:1px; background:#0B1240; transform:scaleX(1); transform-origin:right; transition:transform .35s ease; }
        .preview-scope a.link-underline:hover::after { transform: scaleX(0); transform-origin:left; }
        .preview-scope .accent { color: #E85D0C; }
        .preview-scope .btn-solid { background:#0B1240; color:#fff; padding:14px 22px; display:inline-flex; align-items:center; gap:10px; font-weight:600; font-size:14px; transition: background .2s ease; }
        .preview-scope .btn-solid:hover { background:#E85D0C; }
        .preview-scope .btn-ghost { color:#0B1240; padding:14px 22px; display:inline-flex; align-items:center; gap:10px; font-weight:600; font-size:14px; border-bottom:1px solid #0B1240; }
        .preview-scope .eyebrow { font-size: 11px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: #6B7186; font-family: 'JetBrains Mono', monospace; }
      `}</style>

      {/* HEADER — transparent logo, no white box */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E7E4DD]">
        <div className="max-w-[1280px] mx-auto px-8 h-24 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center shrink-0" data-testid="preview-logo">
            <img src={LOGO} alt="GeekCreative by Borrow A Geek" className="h-16 md:h-20 w-auto block" style={{ background: 'transparent' }} />
          </Link>
          <nav className="hidden md:flex items-center gap-10 text-sm">
            <a href="#services" className="link-underline">Services</a>
            <a href="#design" className="link-underline">Design</a>
            <a href="#web" className="link-underline">Web</a>
            <a href="#contact" className="link-underline">Contact</a>
          </nav>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/login" data-testid="preview-signin" className="link-underline">Sign in</Link>
            <Link to="/register" data-testid="preview-start" className="btn-solid">Start a project <ArrowUpRight size={16} weight="bold"/></Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-[1280px] mx-auto px-8 pt-24 pb-20">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-9">
            <div className="eyebrow mb-8">A creative studio · Kilrush, Co. Clare</div>
            <h1 className="serif text-[54px] md:text-[88px] lg:text-[112px] leading-[0.95]">
              Design that <span className="italic accent">works</span>.<br />
              Print that <span className="italic">lasts</span>.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-3">
            <p className="text-base md:text-lg text-[#3B4066] leading-relaxed">
              GeekCreative is the client portal for every project Borrow&nbsp;A&nbsp;Geek delivers — brief, review, sign off, and ship in one place.
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              <Link to="/register" data-testid="preview-hero-start" className="btn-solid">Start a project <ArrowUpRight size={16} weight="bold"/></Link>
              <a href="#services" className="btn-ghost">See services</a>
            </div>
          </div>
        </div>
      </section>

      {/* HERO IMAGE - full bleed, no navy side bars */}
      <section className="w-full">
        <img
          src={HERO_IMAGE}
          alt="Design. Websites. Print. — Creative solutions that work"
          className="w-full h-auto block"
          data-testid="hero-image"
        />
      </section>

      {/* DESIGN SERVICES */}
      <section id="services" className="bg-[#FAF7F1]">
        <div className="max-w-[1280px] mx-auto px-8 py-24">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-4">
              <div className="eyebrow mb-4">01 · Design</div>
              <h2 className="serif text-5xl md:text-6xl leading-tight">
                Graphic<br/>Design &<br/><span className="italic accent">Print</span>.
              </h2>
              <p className="mt-8 text-[#3B4066] max-w-sm leading-relaxed">
                Logos, brochures, signage, packaging, stationery — whatever needs to look sharp on paper, on a wall, or on a shelf. We design it, we print it, we deliver it.
              </p>
              <Link to="/register" data-testid="design-cta" className="btn-solid mt-8">Request a quote <ArrowUpRight size={16} weight="bold"/></Link>
            </div>
            <div className="col-span-12 lg:col-span-8">
              <ul>
                {DESIGN_SERVICES.map((c, i) => (
                  <li key={c} className="border-t border-[#E7E4DD] py-6 md:py-8 flex items-center justify-between group cursor-pointer">
                    <div className="flex items-baseline gap-6 md:gap-10">
                      <span className="num text-2xl accent w-8">{String(i+1).padStart(2, '0')}</span>
                      <span className="serif text-2xl md:text-4xl group-hover:accent transition-colors">{c}</span>
                    </div>
                    <ArrowUpRight size={20} className="text-[#6B7186] group-hover:text-[#E85D0C] transition-colors" />
                  </li>
                ))}
                <li className="border-t border-[#E7E4DD]" />
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WEB SERVICES */}
      <section id="web" className="max-w-[1280px] mx-auto px-8 py-24">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8 lg:order-2">
            <ul>
              {WEB_SERVICES.map((c, i) => (
                <li key={c} className="border-t border-[#E7E4DD] py-6 md:py-8 flex items-center justify-between group cursor-pointer">
                  <div className="flex items-baseline gap-6 md:gap-10">
                    <span className="num text-2xl accent w-8">{String(i+1).padStart(2, '0')}</span>
                    <span className="serif text-2xl md:text-4xl group-hover:accent transition-colors">{c}</span>
                  </div>
                  <ArrowUpRight size={20} className="text-[#6B7186] group-hover:text-[#E85D0C] transition-colors" />
                </li>
              ))}
              <li className="border-t border-[#E7E4DD]" />
            </ul>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:order-1">
            <div className="eyebrow mb-4">02 · Web</div>
            <h2 className="serif text-5xl md:text-6xl leading-tight">
              Web<br/>Design &<br/><span className="italic accent">Build</span>.
            </h2>
            <p className="mt-8 text-[#3B4066] max-w-sm leading-relaxed">
              Fast, modern websites for small and medium businesses — from a simple one-pager to a full e-commerce store. Built to be found, easy to update, hosted with us.
            </p>
            <Link to="/register" data-testid="web-cta" className="btn-solid mt-8">Request a quote <ArrowUpRight size={16} weight="bold"/></Link>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="bg-[#0B1240] text-white">
        <div className="max-w-[1280px] mx-auto px-8 py-24">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-8">
              <div className="eyebrow mb-6" style={{color:'#E85D0C'}}>A word from clients</div>
              <blockquote className="serif italic text-3xl md:text-5xl leading-tight">
                &ldquo;GeekCreative doesn&apos;t just deliver artwork — they run the whole show. Brief to print, we&apos;ve stopped worrying about any of it.&rdquo;
              </blockquote>
              <div className="mt-8 text-sm text-white/70">Sinéad O&apos;Brien · Marketing Director</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="max-w-[1280px] mx-auto px-8 py-32">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-9">
            <div className="eyebrow mb-6">Let&apos;s work together</div>
            <h2 className="serif text-6xl md:text-8xl leading-[0.95]">
              Have a project<br/>in mind?
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-3">
            <p className="text-[#3B4066] mb-6">Create an account and send us a brief. We&apos;ll come back within one working day.</p>
            <Link to="/register" data-testid="preview-cta-start" className="btn-solid">Start a project <ArrowUpRight size={16} weight="bold"/></Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E7E4DD]">
        <div className="max-w-[1280px] mx-auto px-8 py-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-6">
            <img src={LOGO} alt="GeekCreative by Borrow A Geek" className="h-10 w-auto" style={{ background: 'transparent' }} />
            <p className="mt-4 text-sm text-[#3B4066] max-w-md">A creative studio inside Borrow A Geek — design, websites and print, delivered from the west of Ireland.</p>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="eyebrow mb-3">Contact</div>
            <div className="text-sm space-y-1"><div>065 671 2772</div><div>hello@geekcreative.ie</div><div>Kilrush, Co. Clare</div></div>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="eyebrow mb-3">Navigate</div>
            <div className="text-sm space-y-1"><div><a href="#services" className="link-underline">Services</a></div><div><a href="#design" className="link-underline">Design</a></div><div><a href="#web" className="link-underline">Web</a></div><div><Link to="/login" className="link-underline">Sign in</Link></div></div>
          </div>
        </div>
        <div className="border-t border-[#E7E4DD]">
          <div className="max-w-[1280px] mx-auto px-8 py-6 text-xs text-[#6B7186] flex items-center justify-between">
            <div>© {new Date().getFullYear()} Borrow A Geek · GeekCreative</div>
            <div>DESIGN · WEBSITES · PRINT</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
